import * as ko from "knockout";
import {_} from 'lodash';
import {ObjectTypeId, ObjectTypeId2ImportFormatId, OBFUSCATE_VISIBILITY_DURATION, Permission} from "../constants";
import {global, lm} from "../appContext";
import {IRequestObject, IRequestObjectMulti} from "./index";
import {
    IKnockoutComponent,
    IObjectData,
    IObjectType,
    IPromiseRequest,
    IRule,
    ISyncField,
    TBaseOrObservableOrFunctionType,
    TCondition,
    TFieldType,
    TSyncfield
} from "../index";
import {
    _d,
    getAnyValue,
    getDataSourceTypesFromObject,
    getObjectDefinition,
    getObjectType,
    getRawSyncFieldValue,
    getSyncFieldValue,
    getTelLink,
    isNullOrUndefinedOrEmptyString
} from "../util/misc";
import {nsRuleset} from "../component/ruleset/nsRuleset";
import {nsFa} from "../component/fa/fn";
import PromiseRequest from "./PromiseRequest";
import {ImportFormatId} from "../constants/ImportFormats";
import {storeObservable} from "../util/sessionstore";
import {getHash} from "../constants/RouteDefinitions";

export namespace nsSyncFields {

    import i = nsFa.i;

    export type TObjectData_basic = Partial<IObjectData> & {
        id: IObjectData['id']
        type: IObjectData['type']
        syncfields: TSyncfield_basic[]
    }

    export type TSyncfield_basic = Partial<ISyncField> & {
        name: ISyncField['name']
        value: ISyncField['value']
    }

    interface ISyncFieldsPromiseRequest {
        objectTypeIds: ObjectTypeId | ObjectTypeId[];
        languageId?: number;
        importFormatId?: ImportFormatId;
    }

    export function getSyncFields(objectTypeId: ObjectTypeId, languageId: number | null = null, importFormatId: ImportFormatId | null = null): IRequestObject {
        const params = getSyncfieldsRequestParams(objectTypeId, languageId, importFormatId)
        const promise: PromiseRequest = global.getPromiseRequest('getsyncfields');
        const request: Promise<ISyncField[]> = promise.request(params)
        return {promise, request}
    }

    export function getSyncFieldsMulti(objectTypeIds: ObjectTypeId | ObjectTypeId[], languageId: number | null = null, importFormatId: ImportFormatId | null = null): IRequestObjectMulti {
        const ids = Array.isArray(objectTypeIds) ? objectTypeIds : [objectTypeIds];
        const requestObjects: IRequestObject[] = ids.map(id => getSyncFields(id, languageId, importFormatId));

        const requests = requestObjects.map(o => o.request);
        const promises = requestObjects.map(o => o.promise);

        const request = Promise.all(requests)
            .then((data: ISyncField[][]) => {
                return _.flatten(data);
            });

        return {
            promise: {
                isRequesting: ko.pureComputed(() => promises.some(o => o.isRequesting())),
                isError: ko.pureComputed(() => promises.some(o => o.isError())),
                abort: () => promises.forEach(p => p.abort()),
                request: () => Promise.resolve(request),
            },
            promises: promises,
            requests: requests,
            request: request,
        }
    }

    export function getSyncFieldsPromiseRequest(): IPromiseRequest {
        const requestObjects: KnockoutObservableArray<IRequestObject> = ko.observableArray();
        return {
            isRequesting: ko.pureComputed(() => requestObjects()?.some(r => r.promise.isRequesting())),
            isError: ko.pureComputed(() => requestObjects().some(r => r.promise.isError())),
            request: (params: ISyncFieldsPromiseRequest) => {
                const ids = Array.isArray(params.objectTypeIds) ? params.objectTypeIds : [params.objectTypeIds];
                const requestObjects_syncfields = ids.map(objectTypeId => getSyncFields(objectTypeId, params.languageId, params.importFormatId));
                requestObjects(requestObjects_syncfields);
                return Promise.all(requestObjects().map(ro => ro.request)).then((data: ISyncField[][]) => {
                    return data.reduce((arr, a) => arr.concat(a), []);
                });
            },
            abort: () => requestObjects()?.forEach(requestObject => requestObject.promise.abort()),
        }
    }

    export function getStoreSyncfields(objectTypeId: ObjectTypeId, languageId: number | null = null, importFormatId: ImportFormatId | null = null): ISyncField[] | null {
        const params = getSyncfieldsRequestParams(objectTypeId, languageId, importFormatId)
        if (global.cacheManager.isCached('getsyncfields', params, false)) {
            return global.cacheManager.getCache('getsyncfields', params, false)!.data.data
        }
        return null
    }

    export function hasValue(item: ISyncField): boolean {
        return !isNullOrUndefinedOrEmptyString(item.value)
    }

    export function getCount(key: TSyncfield, syncfields: IObjectData['syncfields']): number {
        const val = getRawSyncFieldValue(key, syncfields)
        return getCountByString(val ?? '')
    }

    export function getCountByString(str: string): number {
        return getArrayByString(str).length
    }

    export function getArrayByString(str: string): string[] {
        if (!str) {
            return []
        }
        const ret = str.split('|')
        if (str.endsWith('|')) {
            ret.pop()
        }
        return ret
    }

    export function getDerivedValueComponent(item: ISyncField, options: {
        objectTypeId: ObjectTypeId
        linkable: Boolean
        showIcon?: boolean,
        object?: IObjectData,
        datasources?: string,
    }): IKnockoutComponent | null | undefined {
        if (item.referenceobjecttypeid == ObjectTypeId.Department) {
            return {
                name: "component/variable/department/from-syncfield",
                params: {syncfield: item}
            }
        }
        const linkable = options.linkable ?? false
        const showIcon = options.showIcon ?? true

        let datasources: string | null
        if (options.datasources) {
            datasources = options.datasources
        } else if(options.object) {
            datasources = getDataSourceTypesFromObject(options.object).join(',')
        } else {
            datasources = null
        }

        switch (item.name) {
            case "ApplicationMessageId":
            case "RejectionMessageId": {
                return {
                    name: "component/variable/messagetemplate",
                    params: {id: item.value, linkable: linkable, showIcon: showIcon},
                }
            }
            case 'AvailableToRuleSetId':
            case 'ManagedByRuleSetId':
            case 'MemberRuleSetId': {
                if (!global.pv.isMinSuperManager) {
                    return null
                }
                return {
                    name: "component/variable/ruleset",
                    params: {id: item.value, linkable: linkable, showIcon: showIcon},
                }
            }
            case "APositionManager":
            case "Manager":
            case "PermanantManager": {
                if (!global.pv.isMinSuperEmployee()) {
                    return null
                }
                const strAuthDataSource = getRawSyncFieldValue('AuthDataSource', options?.object?.syncfields ?? []);
                const numAuthDataSource = strAuthDataSource ? Number(strAuthDataSource) : undefined
                const rules: IRule[] = nsRuleset.getRulesForFetchingAManager(item.value, numAuthDataSource)
                return {
                    name: "component/variable/object-by-any",
                    params: {
                        objectTypeId: ObjectTypeId.User,
                        rules: rules,
                        showIcon: showIcon,
                        datasources
                    },
                }
            }
            case 'FormId': {
                if (!global.pv.isMinCompanyAdmin()) {
                    return null
                }
                return {
                    name: 'component/variable/form-template',
                    params: {
                        id: item.value,
                        showIcon: showIcon,
                    }
                }
            }
            case "ManagerOf": {
                if (!global.pv.isMinSuperEmployee()) {
                    return null
                }
                const tuple = getSourceToTargetTuples({
                    srcObjectTypeId: ObjectTypeId.User,
                    trgObjectTypeId: ObjectTypeId.Department,
                    key: 'ManagerOf',
                })[0];
                if (!tuple) {
                    return null;
                }
                const key = tuple[3];
                return {
                    name: "component/variable/object-by-any",
                    params: {
                        objectTypeId: ObjectTypeId.Department,
                        argument: item.value,
                        attribute: key,
                        showIcon: showIcon,
                        datasources
                    },
                }
            }
            case "Parent": {
                if (item.value === "0" || item.value === "Root") {
                    return null;
                }
                if (options.objectTypeId == ObjectTypeId.Group && Number.isInteger(Number(item.value))) {
                    if (!global.pv.isMinSuperManager()) {
                        return null
                    }
                    const isManual = options.object?.manual || getRawSyncFieldValue('Manual', options.object?.syncfields ?? [])?.toLowerCase() === 'true';
                    if (isManual) {
                        return {
                            name: "component/variable/exporttemplate",
                            params: {
                                id: item.value,
                                showIcon: showIcon,
                                linkable: true,
                                showNoData: true,
                            },
                        }
                    } else {
                        if (!global.pv.isMinSuperManager()) {
                            return null
                        }
                        return {
                            name: "component/variable/object-by-any",
                            params: {
                                objectTypeId: ObjectTypeId.Group,
                                argument: item.value,
                                attribute: 'SourceId',
                                showIcon: showIcon,
                                datasources
                            },
                        }
                    }
                }
                break
            }
            case "UserPassword":
            case "Socialsecuritynumber": {
                return {
                    name: "component/toggle-eye/toggle-eye",
                    params: {
                        text: item.value,
                        duration: OBFUSCATE_VISIBILITY_DURATION,
                    },
                }
            }
            case 'AUnitId':
            case "DepartmentNumber":
            case 'ParentDepartmentNumber':
            case 'WwwUrl': {
                if (!global.pv.isMinSuperEmployee()) {
                    return null
                }
                if (item.name == "WwwUrl") {
                    const orgImportFormatId = global.login.selectedOrganization()?.importformat?.id;
                    const isImportFormat_vismaHr8 = orgImportFormatId != null && [
                        ImportFormatId.Visma_HR8,
                        ImportFormatId.Visma_HR8_HI
                    ].includes(orgImportFormatId);
                    if (isImportFormat_vismaHr8) {
                        return null
                    }
                }
                const srcObjectTypeId = item.objecttypeid || options.objectTypeId
                if (srcObjectTypeId == ObjectTypeId.Group) {
                    return null
                }
                if (item.value === "0" || item.value === "Root") {
                    return null
                }
                let rules: IRule[]
                const tuples = getSourceToTargetTuples({
                    key: item.name,
                    srcObjectTypeId: srcObjectTypeId,
                    trgObjectTypeId: ObjectTypeId.Department,
                })
                rules = tuples.map(t => {
                    return nsRuleset.generateRule({
                        argument: item.value,
                        attribute: t[3],
                        argumentoperator: t[4],
                    })
                })
                return {
                    name: "component/variable/object-by-any",
                    params: _.defaults({
                        objectTypeId: ObjectTypeId.Department,
                        linkable: options.linkable,
                        rules: rules,
                        showIcon: showIcon,
                        datasources
                    }),
                }
            }
            case 'AenrollmentOrgId':
            case 'AeduPositionOrgId': {
                if (!global.pv.isMinSuperEmployee()) {
                    return null
                }
                return {
                    name: "component/variable/object-by-any",
                    params: {
                        argument: item.value,
                        argumentoperator: 'Er lik',
                        attribute: 'DepartmentNumber',
                        objectTypeId: ObjectTypeId.Department,
                        icon: 'fa-school',
                        showIcon: showIcon,
                        datasources
                    },
                };
            }
            case 'EmployeeStatusCode': {
                return {
                    name: 'component/variable/employee-status-code',
                    params: {code: item.value},
                }
            }
            case 'CreatedBy': {
                if (!global.pv.isMinSuperEmployee()) {
                    return null
                }
                return {
                    name: "component/variable/object-by-any",
                    params: {
                        objectTypeId: ObjectTypeId.User,
                        rules: [nsRuleset.generateRule({
                            argument: item.value,
                            attribute: 'Id',
                            argumentoperator: 'Er lik',
                        })],
                        showIcon: showIcon,
                        datasources
                    },
                }
            }
        }
        return null;
    }

    interface IDerivedValueContext {
        data(): { syncfields: ISyncField[] };
        objDef: { singular: string };
    }

    export function getDerivedValue(this: IDerivedValueContext, name: TSyncfield, val: ISyncField['value']): string | null {
        if (!val) {
            return null;
        }
        switch (name) {
            case "BusinessNumber":
                return `${i('fa-external-link')} <a href="https://w2.brreg.no/enhet/sok/detalj.jsp?orgnr=${val}" title="${lm.t('see key information from CCR')}" target="_blank">${lm.t('CCR')}</a>`;
            case "Email":
                return `${i('fa-envelope')} <a href="mailto:${val}">${lm.t('send email')}</a>`;
            case "FormId":
                return `${i("fa-brackets")} <a href="${getHash('formtemplatedetails', {id: val})}">${getSyncFieldValue('FormName', this.data().syncfields)}</a>`
            case "Mobile":
                return getTelLink(val, 'fa-mobile');
            case "PrivatePhone":
            case "TelephoneNumber":
            case "WorkPhone":
                return getTelLink(val);
            case "ObjectTypeId":
                return this.objDef.singular;
            case "SourceId": // group
            case "Parent":
            case "DepartmentNumber": // department
            case 'ParentDepartmentNumber':
            case 'WwwUrl':
                if (val === "0" || val === "Root") {
                    return lm.t('root');
                }
                return null;
        }
        return null;
    }

    export function isFunctionObject(objectTypeId: ObjectTypeId): boolean {
        return [ObjectTypeId.Function, ObjectTypeId.FormFunction].includes(objectTypeId);
    }

    export function isReferenceFieldtype(fieldtype: ISyncField['fieldtype']): boolean {
        return fieldtype == 'Reference'
    }

    export const SYNCFIELD_DEFAULT = {
        core: false,
        description: '',
        displayname: '',
        fieldtype: 'String' as TFieldType,
        ignorefilter: true,
        importformatid: 0,
        isoverridablebylevel: Permission.CompanyAdmin,
        issynced: true,
        languageid: 0,
        maxlength: 0,
        metaidentifier: '',
        name: '' as TSyncfield,
        objecttypeid: ObjectTypeId.User,
        parameters: '',
        referencefieldid: 0,
        referenceobjecttypeid: 0,
        required: false,
        syncfielddisplaynameid: 0,
        syncfieldid: 0,
        value: '',
    } satisfies ISyncField

    export function getTranslationIcon(syncfield: nsSyncFields.TSyncfield_basic): string {
        if (lm.selectedLanguage()?.id != syncfield?.languageid) {
            // it's a default language translation
            return '❌'
        }
        if (syncfield) {
            if (syncfield.displayname) {
                return '✅'
            } else {
                return '⚠️'
            }
        } else {
            return '❌'
        }
    }

    export function hasTranslationIcon(str: string): boolean {
        return str.startsWith('✅') || str.startsWith('⚠️') || str.startsWith('❌')
    }

    export async function getSyncfieldIdsFromNames(objectTypeId: ObjectTypeId, ...names: TSyncfield[]): Promise<string> {
        return getSyncFields(objectTypeId)
            .request
            .then((data: ISyncField[]) => {
                return names
                    .map(k => k.toLowerCase())
                    .map(k => {
                        const ret = data.find(sf => sf.name.toLowerCase() == k)
                        if (!ret) {
                            _d.warn(`Cannot map syncfield name to id [${k}]`)
                        }
                        return ret
                    })
                    .filter((sf): sf is ISyncField => Boolean(sf))
                    .map(sf => sf.syncfieldid)
                    .join(',')
            })
    }

    export function getSyncfieldIdsForObjectTypeId(objectTypeId: ObjectTypeId): Promise<string> {
        const displayNames = getObjectDefinition(objectTypeId)?.syncfieldDisplayName ?? [];
        return getSyncfieldIdsFromNames(objectTypeId, ...displayNames)
    }


    export function getSyncfieldsRequestParams(objecttypeid: ObjectTypeId | null = null, languageid: number | null = null, importformatid: ImportFormatId | null = null): {
        objecttypeid: ObjectTypeId,
        languageid: number,
        importformatid: ImportFormatId
    } {
        const _importFormatId = objecttypeid != null ? ObjectTypeId2ImportFormatId[objecttypeid] : undefined;
        if (_importFormatId) {
            importformatid = _importFormatId
        }

        return {
            objecttypeid: objecttypeid ?? ObjectTypeId.User,
            languageid: languageid ?? storeObservable('language')().id,
            importformatid: importformatid ?? global.login.selectedOrganization()?.importformat.id ?? ImportFormatId.Visma,
        }
    }

    export function getTitle(syncfield: TBaseOrObservableOrFunctionType<ISyncField>) {
        const separator: string = '\n· · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · · \n';
        const lf = '\n';
        const item = getAnyValue(syncfield)
        const ret: (string | number)[] = []

        ret.push('((systemfield|uc))')
        ret.push(separator)
        ret.push('((id)):\n')
        ret.push(item.syncfieldid)
        ret.push(separator)
        ret.push('((name)):\n')
        ret.push(item.name)

        const displayname = item.displayname;
        if (displayname) {
            ret.push(separator)
            ret.push('((displayname)):\n')
            ret.push(displayname)
        }

        const syncfielddisplaynameid = item.syncfielddisplaynameid;
        if (syncfielddisplaynameid) {
            ret.push(separator)
            ret.push('((displayname)) ((id|ac)):\n')
            ret.push(syncfielddisplaynameid)
        }

        const description = item.description;
        if (description) {
            ret.push(separator)
            ret.push('((description)):\n')
            ret.push(description)
        }

        return ret.join('')
    }

    export type TSource2TargetTuple = [ObjectTypeId, ObjectTypeId, TSyncfield, TSyncfield, TCondition, ImportFormatId | null];

    const _o = ObjectTypeId
    const Source2Target: ReadonlyArray<TSource2TargetTuple> = [
        [_o.User, _o.Department, 'AUnitId', 'OrgUnitnr', 'Er lik', null],
        [_o.User, _o.Department, 'DepartmentNumber', 'OrgUnitnr', 'Er lik', null],
        [_o.User, _o.Department, 'ManagerOf', 'DepartmentNumber', 'Er lik', null],
        [_o.User, _o.Department, 'ParentDepartmentNumber', 'OrgUnitnr', 'Er lik en av', null],
        [_o.User, _o.Department, 'WwwUrl', 'OrgUnitnr', 'Er lik', null],
        [_o.User, _o.Department, 'AUnitId', 'DepartmentNumber', 'Er lik', ImportFormatId.Visma_HR8],
        [_o.User, _o.Department, 'DepartmentNumber', 'DepartmentNumber', 'Er lik', ImportFormatId.Visma_HR8],
        [_o.User, _o.Department, 'ParentDepartmentNumber', 'DepartmentNumber', 'Er lik', ImportFormatId.Visma_HR8],
        [_o.User, _o.Department, 'WwwUrl', 'DepartmentNumber', 'Er lik', ImportFormatId.Visma_HR8],
        [_o.User, _o.Department, 'ManagerOf', 'SourceId', 'Er lik', ImportFormatId.Visma_Employee_HR],
        [_o.User, _o.Department, 'ManagerOf', 'SourceId', 'Er lik', ImportFormatId.Visma_Employee_HI],
        [_o.Group, _o.User, 'Manager', 'EmployeeNumber', 'Er lik', null],
        [_o.Group, _o.User, 'Manager', 'AEmployeeNumber', 'Er lik en av', null],
        [_o.Group, _o.User, 'Manager', 'SourceId', 'Er lik', null],
        [_o.Group, _o.User, 'Manager', 'UserId', 'Er lik', ImportFormatId.Visma],
        [_o.Group, _o.User, 'Manager', 'AUserId', 'Er lik en av', ImportFormatId.Visma],
        [_o.Group, _o.Department, 'ParentDepartmentNumber', 'SourceId', 'Er lik', null],
        [_o.Department, _o.User, 'Manager', 'EmployeeNumber', 'Er lik', null],
        [_o.Department, _o.User, 'Manager', 'AEmployeeNumber', 'Er lik en av', null],
        [_o.Department, _o.User, 'Manager', 'SourceId', 'Er lik', null],
        [_o.Department, _o.User, 'Manager', 'UserId', 'Er lik', ImportFormatId.Visma],
        [_o.Department, _o.User, 'Manager', 'AUserId', 'Er lik en av', ImportFormatId.Visma],
        [_o.Department, _o.Department, 'ParentDepartmentNumber', 'DepartmentNumber', 'Er lik', null],
        [_o.Department, _o.Department, 'WwwUrl', 'OrgUnitnr', 'Er lik', null],
        [_o.Department, _o.User, 'Manager', 'SourceId', 'Er lik', ImportFormatId.Visma_Employee_HR],
        [_o.Department, _o.User, 'Manager', 'SourceId', 'Er lik', ImportFormatId.Visma_Employee_HI],
    ]

    interface Source2TargetOptions {
        obj?: IObjectData
        key?: TSyncfield
        syncfield?: ISyncField
        syncfields?: ISyncField[]
        srcObjectTypeId?: ObjectTypeId
        trgObjectTypeId?: ObjectTypeId
        importFormatId?: ImportFormatId
        condition?: TCondition
    }

    export function getSourceToTargetTuples(opt: Source2TargetOptions): TSource2TargetTuple[] {
        const srcObjectTypeId: ObjectTypeId = opt.srcObjectTypeId ?? opt.obj?.type?.id ?? opt.syncfield?.objecttypeid ?? ObjectTypeId.User;
        const trgObjectTypeId: ObjectTypeId = opt.trgObjectTypeId ?? ObjectTypeId.User;
        const key: TSyncfield = opt.key ?? opt.syncfield?.name ?? 'Id';
        const tuples = Source2Target.filter(t => {
            return [
                t[0] == srcObjectTypeId,
                t[1] == trgObjectTypeId,
                t[2] == key,
            ].every(Boolean)
        })
        if (!tuples.length) {
            const fallbackKey: TSyncfield = opt.key ?? opt.syncfield?.name ?? key;
            return [[srcObjectTypeId, trgObjectTypeId, fallbackKey, fallbackKey, 'Er lik', null]]
        }
        const importFormatId: ImportFormatId = opt.importFormatId ?? global.login.selectedOrganization()?.importformat?.id ?? ImportFormatId.Visma;
        const hasTuplesForImportFormat = !!tuples.find(t => t[5] == importFormatId);
        if (hasTuplesForImportFormat) {
            return tuples.filter(t => t[5] == importFormatId)
        }
        return tuples.filter(t => t[5] === null)
    }


    export function getKey2TupleValue<T = string>(keys: TSyncfield[], tuples: T[], key: TSyncfield): T {
        const index = keys.indexOf(key)
        if (index == -1) {
            _d.warn('No such key', key, keys)
        }
        return tuples[index] as T;
    }

    export function convertTuplesToObjectData(typeOrId: IObjectType | ObjectTypeId, keys: TSyncfield[], tuples: string[]): TObjectData_basic {
        const v = (k: TSyncfield): string => getKey2TupleValue<string>(keys, tuples, k);
        const syncfields: TSyncfield_basic[] = keys.map(k => ({name: k, value: v(k)}));
        let type: IObjectType
        if (typeof typeOrId == 'number') {
            type = getObjectType(typeOrId)
        } else {
            type = typeOrId
        }
        return {
            id: Number(v('Id')),
            syncfields: syncfields as ISyncField[],
            type: type,
        }
    }

    export function isSyncfieldMutable(syncfield: ISyncField) {
        if (!syncfield) {
            return false
        }
        const nonMutableExceptions: TSyncfield[] = ['Manual', 'ObjectTypeId', 'ExpiryDate']
        if (syncfield.objecttypeid == ObjectTypeId.ApiUser) {
            nonMutableExceptions.push('SourceId')
        }
        if (nonMutableExceptions.includes(syncfield.name)) {
            return false
        }
        const mutableExceptions: TSyncfield[] = ['ExtendLife']
        if (!syncfield.issynced && !mutableExceptions.includes(syncfield.name)) {
            return false
        }
        return true
    }

}